import RefreshIcon from '@mui/icons-material/Refresh'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import { toFilterOptions } from '@util/filter'

import { useStatusConfig } from '../../../status/hooks/useStatusConfig'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { StyledAlert } from '../../../theme/components/StyledAlert'
import { useHoofdleidingValuelists } from '../../../valuelists/hooks/useHoofdleidingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { Balkenplanning } from '../components/Balkenplanning'
import { PlanningFilters } from '../components/PlanningFilters'
import { PlanningHelp } from '../components/PlanningHelp'
import { ProjectStatusTiles } from '../components/ProjectStatusTiles'
import { usePlanningGeblokkeerdeWeken } from '../hooks/usePlanningGeblokkeerdeWeken'
import { useProjectenPlanningSearch } from '../hooks/useProjectenPlanningSearch'

export { Planning }

const Planning = () => {
  const {
    data: { items: projecten, count: projectenCount },
    filters: projectenFilters,
    isError: isErrorProjecten,
    isLoading: isLoadingProjecten,
    onChangeFilters: onChangeProjectenFilters,
    limit: projectenLimit,
    onChangeLimit: onChangeProjectenLimit,
    onRefetch: onRefetchProjecten,
  } = useProjectenPlanningSearch()
  const {
    data: { items: geblokkeerdeWeken },
    isError: isErrorGeblokkeerdeWeken,
    isLoading: isLoadingGeblokkeerdeWeken,
  } = usePlanningGeblokkeerdeWeken(projectenFilters)
  const { data: valuelists } = useValuelists()
  const { data: hoofdleidingValuelists } = useHoofdleidingValuelists()
  const { data: statusConfig } = useStatusConfig()

  const isLoading = isLoadingProjecten || isLoadingGeblokkeerdeWeken

  return (
    <Page
      title={['Planning', 'Hoofdleiding']}
      breadcrumbs={[
        { label: 'Hoofdleiding', href: '/home', tooltip: 'CAPO home' },
        { label: 'Planning' },
      ]}
      gap={4}
      fillScreen
    >
      <PageSection id="planning" heading="Planning">
        <PlanningFilters
          filters={projectenFilters}
          onChange={onChangeProjectenFilters}
          filterOptions={{
            aannemers: valuelists.aannemers,
            combis: hoofdleidingValuelists.verantwoordelijken.filter(
              ({ group }) => group === 'Combi',
            ),
            gebieden: valuelists.gebieden,
            gemeenten: hoofdleidingValuelists.gemeenten,
            labels: toFilterOptions(hoofdleidingValuelists.labels),
            processtappen: statusConfig.processtappenHoofdleiding,
            typeWerkTypes: hoofdleidingValuelists.typeWerkTypes,
            verantwoordelijken: hoofdleidingValuelists.verantwoordelijken,
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <PlanningHelp />

          <Tooltip title="Opnieuw zoeken">
            <Box component="span" sx={{ display: 'inline-block' }}>
              <IconButton
                aria-label="Opnieuw zoeken"
                disabled={isLoadingProjecten}
                onClick={() => onRefetchProjecten()}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </PageSection>

      <ProjectStatusTiles
        projecten={projecten}
        total={projectenCount}
        limit={projectenLimit}
        selected={projectenFilters.processtap}
        onChangeSelected={(processtap) =>
          onChangeProjectenFilters({ ...projectenFilters, processtap })
        }
      />

      {isLoading && (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isErrorGeblokkeerdeWeken && (
        <StyledAlert severity="error" title="Let op!" sx={{ mb: 0 }}>
          Het ophalen van de geblokkeerde weken is mislukt. Geblokkeerde weken
          worden niet getoond in de planning.
        </StyledAlert>
      )}

      {isErrorProjecten && (
        <StyledAlert severity="error" title="Let op!" sx={{ mb: 0 }}>
          Het ophalen van de projecten is mislukt. De planning kan niet worden
          getoond.
        </StyledAlert>
      )}

      {projecten.length < 1 && !isLoading && (
        <StyledAlert severity="info" title="Geen resultaten" sx={{ mb: 0 }}>
          Gebruik de filters om resultaten te vinden.
        </StyledAlert>
      )}

      {projecten.length > 0 && !isLoading && (
        <Balkenplanning
          projects={projecten}
          geblokkeerdeWeken={geblokkeerdeWeken}
          total={projectenCount}
          limit={projectenLimit}
          onChangeLimit={onChangeProjectenLimit}
        />
      )}
    </Page>
  )
}
